/**
 * LocationFunctionToggle - a concrete location's Function as a two-way
 * switch: Temperature, or Maturity. Used in the location
 * tables (each change saves straight away) and the add drawer's channel rows.
 *
 * Maturity is only offered on a pour that computes it. An older location
 * saved as Temperature (ambient) shows as Temperature; it only changes when
 * the user picks the other side.
 */
import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  CONCRETE_MATURITY_LABEL,
  TEMPERATURE_ONLY_LABEL,
  type LocationFunctionValue,
} from '../../utils/locationFunction';
import { BRAND_GREEN } from './pourWizardStyles';

export interface LocationFunctionToggleProps {
  value: LocationFunctionValue;
  onChange: (value: LocationFunctionValue) => void;
  maturityAllowed: boolean;
  /** How the pour calculates maturity ("Temperature-Time Factor"), for the
   *  maturity side's tooltip. */
  maturityName?: string;
  disabled?: boolean;
  /** Names the switch for screen readers, e.g. "Channel 2 function". */
  ariaLabel?: string;
}

const LocationFunctionToggle: React.FC<LocationFunctionToggleProps> = ({
  value,
  onChange,
  maturityAllowed,
  maturityName,
  disabled,
  ariaLabel = 'Function',
}) => {
  const shown = value === 'maturity' ? 'maturity' : 'temp';
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={shown}
      aria-label={ariaLabel}
      onChange={(_, next: LocationFunctionValue | null) => {
        if (next && next !== shown) onChange(next);
      }}
      sx={{
        bgcolor: '#fff',
        '& .MuiToggleButton-root': {
          py: 0.25,
          px: 1,
          fontSize: '0.72rem',
          lineHeight: 1.6,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          color: 'text.secondary',
        },
        '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
          bgcolor: BRAND_GREEN,
          color: '#1a1a2e',
          fontWeight: 600,
        },
      }}
    >
      <ToggleButton value="temp" disabled={disabled}>
        {TEMPERATURE_ONLY_LABEL}
      </ToggleButton>
      {/* The group sets selected / onChange on its direct children, so the
          hint is a plain title rather than a Tooltip wrapper. */}
      <ToggleButton
        value="maturity"
        disabled={disabled || !maturityAllowed}
        title={
          !maturityAllowed
            ? 'This pour does not calculate maturity'
            : maturityName
              ? `Maturity calculated as ${maturityName}`
              : undefined
        }
      >
        {CONCRETE_MATURITY_LABEL}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default LocationFunctionToggle;
